import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import FontFamily from '@tiptap/extension-font-family';
import { TextStyle } from '@tiptap/extension-text-style';
import { Extension } from '@tiptap/core';

// Custom extension for font size
const FontSize = Extension.create({
  name: 'fontSize',

  addGlobalAttributes() {
    return [
      {
        types: ['textStyle'],
        attributes: {
          fontSize: {
            default: null,
            parseHTML: element => element.style.fontSize,
            renderHTML: attributes => {
              if (!attributes.fontSize) {
                return {};
              }
              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
          },
        },
      },
    ];
  },
});

interface TipTapEditorProps {
  content: string;
  isEditing: boolean;
  onUpdate: (content: string) => void;
  style?: React.CSSProperties;
  className?: string;
  onEditorReady?: (editor: ReturnType<typeof useEditor>) => void;
}

export const TipTapEditor: React.FC<TipTapEditorProps> = ({
  content,
  isEditing,
  onUpdate,
  style = {},
  className = '',
  onEditorReady,
}) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      FontFamily,
      TextStyle,
      FontSize,
    ],
    content: content,
    editable: isEditing,
    onUpdate: ({ editor }) => {
      const json = editor.getJSON();
      onUpdate(JSON.stringify(json));
    },
    editorProps: {
      attributes: {
        class: 'tiptap-editor focus:outline-none',
        style: Object.entries(style)
          .map(([key, value]) => `${key.replace(/([A-Z])/g, '-$1').toLowerCase()}: ${value}`)
          .join('; '),
      },
    },
  });

  useEffect(() => {
    if (editor && !isEditing) {
      editor.setEditable(false);
    } else if (editor && isEditing) {
      editor.setEditable(true);
    }
  }, [isEditing, editor]);

  useEffect(() => {
    if (editor && content !== JSON.stringify(editor.getJSON())) {
      try {
        const parsedContent = JSON.parse(content);
        editor.commands.setContent(parsedContent);
      } catch {
        editor.commands.setContent(content);
      }
    }
  }, [content, editor]);

  useEffect(() => {
    if (editor) {
      onEditorReady?.(editor);
    }
  }, [editor, onEditorReady]);

  if (!editor) {
    return null;
  }

  return (
    <div className={`relative w-full ${className}`}>
      <EditorContent editor={editor} className="w-full" />
    </div>
  );
};
